import { useCallback, useEffect, useRef, useState } from "react";
import { getCandidates } from "../services/candidateApi";
import { mapCandidateFromBackend } from "../utils/candidateMapper";
import {
    CANDIDATES_PAGE_SIZE,
    normalizeCandidatesPage,
} from "../utils/candidatesPagination";

function isAbortError(err) {
    return err?.name === "AbortError" || /abort/i.test(String(err?.message || ""));
}

/**
 * Abortable paginated candidates loader. Cancels in-flight list requests on filter change.
 */
export function useCandidatesList({
    statusFilter,
    debouncedSearch,
    roleFilter,
    vacancyIds,
    onlyPerfect,
    onlyNotSync,
    categoryFilter,
    includeArchived,
    extraFilters = null,
    perPage = CANDIDATES_PAGE_SIZE,
    onError,
}) {
    const [candidates, setCandidates] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [total, setTotal] = useState(null);

    const pageRef = useRef(0);
    const abortRef = useRef(null);
    const requestIdRef = useRef(0);
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;

    const vacancyIdsKey = Array.isArray(vacancyIds)
        ? vacancyIds.map(String).filter(Boolean).sort().join(",")
        : vacancyIds
          ? String(vacancyIds)
          : "";
    // правка №4/№14: вкладки воронки, доп. фильтры и сортировка — одним ключом
    const extraKey = extraFilters ? JSON.stringify(extraFilters) : "";

    useEffect(() => {
        pageRef.current = page;
    }, [page]);

    const loadCandidatesPage = useCallback(
        async (pageNum) => {
            abortRef.current?.abort();
            abortRef.current = new AbortController();
            const signal = abortRef.current?.signal;
            const requestId = ++requestIdRef.current;
            const ids = vacancyIdsKey
                ? vacancyIdsKey.split(",").map((id) => {
                      const n = Number(id);
                      return Number.isFinite(n) ? n : id;
                  })
                : null;

            try {
                setListLoading(true);

                const data = await getCandidates(
                    statusFilter,
                    debouncedSearch,
                    roleFilter,
                    ids,
                    onlyPerfect ? true : undefined,
                    pageNum,
                    perPage,
                    categoryFilter || null,
                    {
                        ...(extraKey ? JSON.parse(extraKey) : {}),
                        signal,
                        isNotSync: onlyNotSync ? true : undefined,
                        includeArchived: includeArchived ? true : undefined,
                    }
                );

                // Stale / aborted response — ignore
                if (requestId !== requestIdRef.current || signal?.aborted) return;

                const normalized = normalizeCandidatesPage(data, {
                    page: pageNum,
                    perPage,
                });
                const mapped = normalized.items.map((candidate) => mapCandidateFromBackend(candidate));

                setCandidates(mapped);
                setHasMore(normalized.hasMore);
                setTotal(normalized.total);
                setPage(pageNum);
            } catch (e) {
                if (isAbortError(e) || signal?.aborted || requestId !== requestIdRef.current) return;
                onErrorRef.current?.(e);
                setCandidates([]);
                setHasMore(false);
                setTotal(0);
            } finally {
                // Only the latest in-flight request may clear loading flags
                if (requestId === requestIdRef.current) {
                    setListLoading(false);
                }
            }
        },
        [
            statusFilter,
            debouncedSearch,
            roleFilter,
            vacancyIdsKey,
            onlyPerfect,
            onlyNotSync,
            categoryFilter,
            includeArchived,
            extraKey,
            perPage,
        ]
    );

    const goToPage = useCallback((nextPage) => {
        if (!Number.isInteger(nextPage) || nextPage < 0) return;
        if (typeof total === "number" && nextPage >= Math.max(1, Math.ceil(total / perPage))) return;
        if (nextPage === pageRef.current) return;
        loadCandidatesPage(nextPage);
    }, [total, perPage, loadCandidatesPage]);

    const reloadFirstPage = useCallback(() => {
        setPage(0);
        setHasMore(true);
        loadCandidatesPage(0);
    }, [loadCandidatesPage]);

    // Debounced reload when filters change (also absorbs React StrictMode double-mount).
    useEffect(() => {
        const timer = setTimeout(() => {
            setPage(0);
            setHasMore(true);
            loadCandidatesPage(0);
        }, 120);
        return () => {
            clearTimeout(timer);
            abortRef.current?.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, debouncedSearch, roleFilter, vacancyIdsKey, onlyPerfect, onlyNotSync, categoryFilter, includeArchived, extraKey, perPage]);

    useEffect(() => {
        return () => abortRef.current?.abort();
    }, []);

    return {
        candidates,
        setCandidates,
        listLoading,
        page,
        totalPages: typeof total === "number" ? Math.ceil(total / perPage) : 0,
        hasMore,
        total,
        setTotal,
        goToPage,
        reloadFirstPage,
        loadCandidatesPage,
    };
}
